import React, { useState, useEffect, useCallback } from 'react';
import Icon from './icons';
import { useTranslation } from 'react-i18next';

interface PatientEvent {
  id: string;
  date: string;
  time: string;
  category: 'admission' | 'diagnosis' | 'treatment' | 'test' | 'status' | 'discharge';
  title: string;
  description: string;
  indicators?: {
    name: string;
    value: string;
    unit: string;
    status?: 'normal' | 'warning' | 'critical';
  }[];
}

interface AIBriefingProps {
  patientId: string;
  patientName?: string;
}

const AIBriefing: React.FC<AIBriefingProps> = ({ patientId, patientName }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<PatientEvent[]>([]);
  const [groupedEvents, setGroupedEvents] = useState<{ [date: string]: PatientEvent[] }>({});

  // Generate mock events for demonstration - memoized with useCallback
  const generateMockEvents = useCallback((patientId: string): PatientEvent[] => {
    // Get current date and subtract days to create a timeline
    const today = new Date();
    const day1 = new Date(today);
    day1.setDate(today.getDate() - 2);
    const day2 = new Date(today);
    day2.setDate(today.getDate() - 1);
    const day3 = new Date(today);
    
    // Format dates as YYYY-MM-DD
    const formatDate = (date: Date) => {
      return date.toISOString().split('T')[0];
    };
    
    const day1Date = formatDate(day1);
    const day2Date = formatDate(day2);
    const day3Date = formatDate(day3);
    
    return [
      {
        id: '1',
        date: day1Date,
        time: '08:30',
        category: 'admission',
        title: t('aiBriefing.events.admission.title'),
        description: t('aiBriefing.events.admission.description')
      },
      {
        id: '2',
        date: day1Date,
        time: '10:15',
        category: 'test',
        title: t('aiBriefing.events.ecg.title'),
        description: t('aiBriefing.events.ecg.description')
      },
      {
        id: '3',
        date: day1Date,
        time: '14:30',
        category: 'test',
        title: t('aiBriefing.events.bloodTest.title'),
        description: t('aiBriefing.events.bloodTest.description'),
        indicators: [
          { name: t('aiBriefing.indicators.troponin'), value: '0.12', unit: 'ng/mL', status: 'warning' },
          { name: t('aiBriefing.indicators.bnp'), value: '420', unit: 'pg/mL', status: 'critical' },
          { name: t('aiBriefing.indicators.creatinine'), value: '92', unit: 'μmol/L', status: 'normal' }
        ]
      },
      {
        id: '4',
        date: day1Date,
        time: '16:45',
        category: 'diagnosis',
        title: t('aiBriefing.events.initialDiagnosis.title'),
        description: t('aiBriefing.events.initialDiagnosis.description')
      },
      {
        id: '5',
        date: day1Date,
        time: '18:00',
        category: 'treatment',
        title: t('aiBriefing.events.treatmentPlan.title'),
        description: t('aiBriefing.events.treatmentPlan.description')
      },
      {
        id: '6',
        date: day2Date,
        time: '09:00',
        category: 'status',
        title: t('aiBriefing.events.statusAssessment1.title'),
        description: t('aiBriefing.events.statusAssessment1.description'),
        indicators: [
          { name: t('aiBriefing.indicators.bloodPressure'), value: '145/85', unit: 'mmHg', status: 'warning' },
          { name: t('aiBriefing.indicators.heartRate'), value: '78', unit: t('aiBriefing.units.bpm'), status: 'normal' },
          { name: t('aiBriefing.indicators.respiratoryRate'), value: '20', unit: t('aiBriefing.units.bpm'), status: 'normal' },
          { name: t('aiBriefing.indicators.oxygenSaturation'), value: '95', unit: '%', status: 'normal' }
        ]
      },
      {
        id: '7',
        date: day2Date,
        time: '10:30',
        category: 'test',
        title: t('aiBriefing.events.echocardiogram.title'),
        description: t('aiBriefing.events.echocardiogram.description')
      },
      {
        id: '8',
        date: day2Date,
        time: '14:00',
        category: 'treatment',
        title: t('aiBriefing.events.treatmentAdjustment.title'),
        description: t('aiBriefing.events.treatmentAdjustment.description')
      },
      {
        id: '9',
        date: day2Date,
        time: '16:30',
        category: 'test',
        title: t('aiBriefing.events.coronaryCT.title'),
        description: t('aiBriefing.events.coronaryCT.description')
      },
      {
        id: '10',
        date: day3Date,
        time: '08:30',
        category: 'status',
        title: t('aiBriefing.events.statusAssessment2.title'),
        description: t('aiBriefing.events.statusAssessment2.description'),
        indicators: [
          { name: t('aiBriefing.indicators.bloodPressure'), value: '135/80', unit: 'mmHg', status: 'normal' },
          { name: t('aiBriefing.indicators.heartRate'), value: '65', unit: t('aiBriefing.units.bpm'), status: 'normal' },
          { name: t('aiBriefing.indicators.respiratoryRate'), value: '18', unit: t('aiBriefing.units.bpm'), status: 'normal' },
          { name: t('aiBriefing.indicators.oxygenSaturation'), value: '98', unit: '%', status: 'normal' },
          { name: t('aiBriefing.indicators.bnp'), value: '280', unit: 'pg/mL', status: 'warning' }
        ]
      },
      {
        id: '11',
        date: day3Date,
        time: '11:00',
        category: 'diagnosis',
        title: t('aiBriefing.events.multidisciplinaryConsultation.title'),
        description: t('aiBriefing.events.multidisciplinaryConsultation.description')
      },
      {
        id: '12',
        date: day3Date,
        time: '15:30',
        category: 'treatment',
        title: t('aiBriefing.events.treatmentPlanFinalized.title'),
        description: t('aiBriefing.events.treatmentPlanFinalized.description')
      }
    ];
  }, [t]);

  useEffect(() => {
    // In a real application, this would fetch data from an API
    const fetchPatientTimeline = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Mock data for demonstration
        const mockEvents: PatientEvent[] = generateMockEvents(patientId);
        setEvents(mockEvents);
        
        // Group events by date
        const grouped = mockEvents.reduce((acc, event) => {
          if (!acc[event.date]) {
            acc[event.date] = [];
          }
          acc[event.date].push(event);
          return acc;
        }, {} as { [date: string]: PatientEvent[] });
        
        setGroupedEvents(grouped);
      } catch (error) {
        console.error('Error fetching patient timeline:', error);
        setError(t('aiBriefing.loadError'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchPatientTimeline();
  }, [patientId, t, generateMockEvents]);

  // Get category icon and color
  const getCategoryInfo = (category: PatientEvent['category']) => {
    switch (category) {
      case 'admission':
        return { icon: 'Login', color: 'text-blue-500', bgColor: 'bg-blue-100' };
      case 'diagnosis':
        return { icon: 'HealthAndSafety', color: 'text-purple-500', bgColor: 'bg-purple-100' };
      case 'treatment':
        return { icon: 'Medication', color: 'text-green-500', bgColor: 'bg-green-100' };
      case 'test':
        return { icon: 'Science', color: 'text-amber-500', bgColor: 'bg-amber-100' };
      case 'status':
        return { icon: 'MonitorHeart', color: 'text-indigo-500', bgColor: 'bg-indigo-100' };
      case 'discharge':
        return { icon: 'Logout', color: 'text-teal-500', bgColor: 'bg-teal-100' };
      default:
        return { icon: 'Event', color: 'text-gray-500', bgColor: 'bg-gray-100' };
    }
  };

  // Get status color for indicators
  const getStatusColor = (status?: 'normal' | 'warning' | 'critical') => {
    switch (status) {
      case 'normal':
        return 'text-green-600';
      case 'warning':
        return 'text-amber-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md m-4" role="alert">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">{t('aiBriefing.title')}</h2>
        <p className="mt-1 text-sm text-gray-600">
          {t('aiBriefing.description')}
        </p>
      </div>
      
      {/* Timeline */}
      <div className="flow-root">
        <ul className="-mb-8">
          {Object.keys(groupedEvents).sort().map((date, dateIndex, datesArray) => (
            <React.Fragment key={date}>
              {/* Date header */}
              <li className="relative pb-4">
                <div className="relative flex items-center space-x-3">
                  <div>
                    <div className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center ring-8 ring-white">
                      <Icon iconName="CalendarToday" className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-medium text-gray-900">
                      {new Date(date).toLocaleDateString(t('common.locale'), { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
                    </h3>
                  </div>
                </div>
              </li>
              
              {/* Events for this date */}
              {groupedEvents[date]
                .sort((a, b) => a.time.localeCompare(b.time))
                .map((event, eventIndex, eventsArray) => {
                  const { icon, color, bgColor } = getCategoryInfo(event.category);
                  const isLastEventOfDay = eventIndex === eventsArray.length - 1;
                  const isLastDay = dateIndex === datesArray.length - 1;
                  
                  return (
                    <li key={event.id} className="relative pb-8">
                      {!(isLastEventOfDay && isLastDay) && (
                        <span
                          className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        />
                      )}
                      <div className="relative flex space-x-3">
                        <div>
                          <div className={`h-8 w-8 rounded-full ${bgColor} flex items-center justify-center ring-8 ring-white`}>
                            <Icon iconName={icon} className={`h-5 w-5 ${color}`} />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                          <div className="pr-4">
                            <div className="text-sm font-medium text-gray-900">
                              {event.time} - {event.title}
                            </div>
                            <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">
                              {event.description}
                            </p>
                            
                            {/* Indicators */}
                            {event.indicators && event.indicators.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {event.indicators.map((indicator, i) => (
                                  <span
                                    key={i}
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(indicator.status)} border-current`}
                                  >
                                    {indicator.name}: {indicator.value} {indicator.unit}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
            </React.Fragment>
          ))}
        </ul>
      </div>
      
      {/* Current status summary */}
      {events.length > 0 && (
        <div className="mt-6 bg-indigo-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-indigo-900">{t('aiBriefing.currentStatusTitle')}</h3>
          <p className="mt-2 text-sm text-indigo-800">
            {t('aiBriefing.currentStatusSummary')}
          </p>
        </div>
      )}
    </div>
  );
};

export default AIBriefing; 